import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  GOV_RX_STATUS_LABEL,
  govRxApi,
  govRxQueryKeys,
  type GovRxSubmitResult,
} from "../../../shared/api/govRx";
import { apiError } from "../../../shared/api/client";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Đơn thuốc quốc gia (donthuocquocgia.vn) panel — ADR-0019. Rendered only for
 * SIGNED prescriptions. Pro-gated: a 402 from the status call renders the
 * upgrade card instead. Automated submit is honest about the not-provisioned
 * fallback to manual export.
 */
export function GovRxPanel({ prescriptionId }: { prescriptionId: string }) {
  const qc = useQueryClient();
  const [result, setResult] = useState<GovRxSubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const status = useQuery({
    queryKey: govRxQueryKeys.status(prescriptionId),
    queryFn: () => govRxApi.status(prescriptionId),
    retry: false,
  });

  const submit = useMutation({
    mutationFn: () => govRxApi.submit(prescriptionId),
    onSuccess: (r) => {
      setResult(r);
      setError(null);
      qc.invalidateQueries({ queryKey: govRxQueryKeys.status(prescriptionId) });
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  const download = useMutation({
    mutationFn: () => govRxApi.downloadExport(prescriptionId),
    onSuccess: () =>
      qc.invalidateQueries({
        queryKey: govRxQueryKeys.status(prescriptionId),
      }),
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  // Pro-gate: 402 → upgrade CTA, not an error.
  if (status.isError && apiError(status.error).status === 402) {
    return (
      <Card className="mt-6 bg-muted">
        <CardContent className="p-4">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            Đơn thuốc quốc gia (donthuocquocgia.vn)
          </h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Kết nối Hệ thống đơn thuốc quốc gia (Thông tư 04/2022/TT-BYT) thuộc
            gói <strong className="font-semibold">Pro</strong>. Liên hệ Addy để
            nâng cấp.
          </p>
        </CardContent>
      </Card>
    );
  }

  const s = status.data;

  return (
    <Card className="mt-6">
      <CardContent className="p-4">
        <div className="mb-3 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold tracking-tight text-foreground">
            Đơn thuốc quốc gia (donthuocquocgia.vn)
          </h2>
          {s && (
            <Badge
              variant={
                s.status === "ACCEPTED"
                  ? "success"
                  : s.status === "REJECTED" || s.status === "FAILED"
                    ? "destructive"
                    : "warning"
              }
            >
              {GOV_RX_STATUS_LABEL[s.status] ?? s.status}
            </Badge>
          )}
        </div>

        {status.isLoading && (
          <p className="text-sm text-muted-foreground">Đang tải…</p>
        )}

        <p className="text-[13px] text-muted-foreground">
          Xuất XML theo định dạng quốc gia để nộp qua cổng. Chữ ký số XAdES và
          gửi API tự động sẽ bật khi hạ tầng ký số + cấp phép kết nối hoàn tất
          (ADR-0019). Hệ thống không bao giờ báo “đã gửi” giả.
        </p>

        <div className="mt-3 flex flex-wrap gap-2">
          <Button
            type="button"
            disabled={download.isPending}
            onClick={() => {
              setError(null);
              download.mutate();
            }}
          >
            {download.isPending ? "Đang tạo…" : "Tải XML đơn thuốc quốc gia"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={submit.isPending}
            onClick={() => {
              setError(null);
              submit.mutate();
            }}
          >
            {submit.isPending ? "Đang gửi…" : "Gửi tới donthuocquocgia.vn"}
          </Button>
        </div>

        {result && (
          <div
            className={`mt-3 rounded-md border px-3 py-2 text-sm ${
              result.accepted
                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                : "border-amber-200 bg-amber-50 text-amber-700"
            }`}
            role="status"
          >
            {result.message}
          </div>
        )}

        {error && (
          <p className="mt-3 text-sm text-destructive">{error}</p>
        )}

        {s && (s.nationalTxId || s.errorDetail) && (
          <p className="mt-3 text-[13px] text-muted-foreground">
            {s.nationalTxId && (
              <>
                Mã giao dịch QG:{" "}
                <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                  {s.nationalTxId}
                </code>
                .{" "}
              </>
            )}
            {s.errorDetail}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
